<div class="flex flex-column gap-3" data-test="ql-builder-panel">

    <!-- header -->
    <div class="flex flex-wrap align-items-center justify-content-between gap-2">
        <div class="flex align-items-center gap-2">
            <span class="text-lg font-semibold">Abfrage</span>
            @if (envBound) {
                <p-tag value="umgebungsgebunden" severity="warn" icon="fa-regular fa-link"
                       [pTooltip]="textEnvBound" tooltipPosition="bottom"
                       data-test="ql-builder-env-bound"></p-tag>
            }
        </div>
        @if (model) {
            <p-button label="Neu beginnen" icon="fa-regular fa-eraser" severity="secondary" [text]="true" size="small"
                      pTooltip="Start-Entität und alle Einstellungen verwerfen"
                      (onClick)="clearModel()"></p-button>
        }
    </div>

    <!-- start entity -->
    <div class="flex flex-column gap-1">
        <label class="font-medium">Start-Entität</label>
        <!-- tracked by startEntityKey: an emptied model recreates the input blank (N6, see TS). The key is a
             derived string, not the item itself: the recreation is intended, and an identity track makes Angular
             warn about it in dev mode (NG0956, Q3-10) -->
        @for (key of [startEntityKey]; track 'start-entity-' + key) {
            <mvs-object-type [objectTypeAlias]="model?.root?.entityName ?? ''"
                             (objectTypeChange)="onStartEntityChange($event)"></mvs-object-type>
        }
        <span class="text-sm text-color-secondary">Mindestens 3 Zeichen eingeben, z. B. „tm.Ticket“.</span>
    </div>

    @if (!model) {
        <p-message severity="info" styleClass="w-full" data-test="ql-builder-empty">
            <div class="flex flex-column gap-1">
                <span class="font-semibold">Noch keine Abfrage.</span>
                <span>Wählen Sie eine Start-Entität – danach Felder, Filter und Verknüpfungen ergänzen.</span>
            </div>
        </p-message>
    } @else {

        <!-- grouping mode -->
        <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg">
            <div class="flex align-items-center justify-content-between gap-2">
                <div class="flex align-items-center gap-2">
                    <span [pTooltip]="!groupByMode && groupByBlockedReason ? groupByBlockedReason : ''">
                        <p-toggleSwitch inputId="qlBuilderGroupBy"
                                        [ngModel]="groupByMode"
                                        [disabled]="!groupByMode && !!groupByBlockedReason"
                                        (ngModelChange)="onGroupByToggle($event)"
                                        data-test="ql-builder-group-by-toggle"></p-toggleSwitch>
                    </span>
                    <label for="qlBuilderGroupBy" class="font-medium">Gruppieren/Aggregieren</label>
                </div>
                <span class="text-sm text-color-secondary">für Diagramm und Kategorie</span>
            </div>
            @if (!groupByMode && groupByBlockedReason) {
                <span class="text-sm text-color-secondary">{{ groupByBlockedReason }}</span>
            }
            @if (groupByHint; as hint) {
                <p-message severity="info" styleClass="w-full" size="small" data-test="ql-builder-group-by-hint">
                    <div class="flex flex-column gap-2 w-full">
                        @if (hint.sortingsRemoved) {
                            <span data-test="ql-builder-group-by-hint-sorting">
                                Die Sortierung wurde entfernt – sie bezog sich auf Spalten des Zeilenmodus. Unten kann eine neue Sortierung
                                auf Gruppierungsfelder oder Kennzahlen gesetzt werden.
                            </span>
                        }
                        @if (hint.joinKeys.length) {
                            <span data-test="ql-builder-group-by-hint-join-keys">
                                Die Felder der Verknüpfungen werden zu weiteren Gruppenschlüsseln: {{ hint.joinKeys.join(', ') }}.
                                Jede Kombination ergibt eine eigene Gruppe.
                            </span>
                            <div>
                                <p-button label="Zusätzliche Schlüssel entfernen" icon="fa-regular fa-eraser" size="small" severity="secondary"
                                          [outlined]="true" data-test="ql-builder-clear-join-fields"
                                          (onClick)="clearJoinFields()"></p-button>
                            </div>
                        }
                        <div>
                            <p-button label="Verstanden" size="small" severity="secondary" [text]="true"
                                      (onClick)="dismissGroupByHint()"></p-button>
                        </div>
                    </div>
                </p-message>
            }

            @if (emptyJoinHint; as emptyJoins) {
                <p-message severity="info" styleClass="w-full" size="small" data-test="ql-builder-empty-join-hint">
                    <div class="flex flex-column gap-2 w-full">
                        <span>
                            {{ emptyJoins.length === 1 ? 'Diese Verknüpfung hat' : 'Diese Verknüpfungen haben' }} keine Felder mehr:
                            {{ emptyJoins.join(', ') }}. Sie wirkt trotzdem auf die Zeilen (eine 1:n-Verknüpfung vervielfacht sie,
                            eine innere schränkt sie ein) und damit auf die Kennzahlen – entfernen, wenn sie nur für die Schlüssel gedacht war.
                        </span>
                        <div class="flex flex-wrap gap-2">
                            <p-button [label]="emptyJoins.length === 1 ? 'Leere Verknüpfung entfernen' : 'Leere Verknüpfungen entfernen'"
                                      icon="fa-regular fa-trash-can" size="small" severity="secondary" [outlined]="true"
                                      data-test="ql-builder-remove-empty-joins" (onClick)="removeEmptyJoins()"></p-button>
                            <p-button label="Behalten" size="small" severity="secondary" [text]="true"
                                      (onClick)="dismissEmptyJoinHint()"></p-button>
                        </div>
                    </div>
                </p-message>
            }

            @if (model.groupBy; as groupBy) {
                @if (rootMetaState === 'loading') {
                    <p-skeleton height="2.5rem"></p-skeleton>
                } @else if (rootMetaState === 'error') {
                    <p-message severity="error" size="small" styleClass="w-full"
                               text="Die Felder der Start-Entität konnten nicht geladen werden."></p-message>
                } @else {
                    <!-- grouping fields -->
                    <span class="font-medium mt-2">Gruppieren nach</span>
                    @for (field of groupBy.fields; track $index; let i = $index) {
                        <div class="flex flex-wrap align-items-center gap-2" [attr.data-test]="'ql-builder-group-field-' + i">
                            <p-select [options]="groupFieldOptions" optionLabel="label" optionValue="value"
                                      [ngModel]="field.name" (ngModelChange)="updateGroupFieldName(i, $event)"
                                      [filter]="true" filterPlaceholder="Suchen" emptyFilterMessage="Keine Treffer"
                                      emptyMessage="Keine Felder vorhanden" appendTo="body" styleClass="flex-1 min-w-0"></p-select>
                            @if (isDateField(field.name)) {
                                <p-select [options]="precisionOptions" optionLabel="label" optionValue="value"
                                          [ngModel]="field.dateGroup?.precision ?? null"
                                          (ngModelChange)="updateGroupFieldPrecision(i, $event)"
                                          appendTo="body" styleClass="w-10rem"></p-select>
                            }
                            <p-button icon="fa-regular fa-trash-can" severity="danger" [text]="true" [rounded]="true"
                                      ariaLabel="Gruppierungsfeld entfernen" (onClick)="removeGroupField(i)"></p-button>
                        </div>
                    } @empty {
                        <span class="text-sm text-color-secondary">
                            Ohne Gruppierungsfeld liefert die Abfrage genau eine Zeile (Gesamtwerte).
                        </span>
                    }
                    <div>
                        <p-button label="Gruppierungsfeld hinzufügen" icon="fa-solid fa-plus" size="small"
                                  severity="secondary" [outlined]="true" [disabled]="!groupFieldOptions.length"
                                  (onClick)="addGroupField()"></p-button>
                    </div>

                    <!-- aggregates -->
                    <span class="font-medium mt-2">Kennzahlen</span>
                    @for (aggregate of groupBy.aggregates; track $index; let i = $index) {
                        <div class="flex flex-wrap align-items-center gap-2" [attr.data-test]="'ql-builder-aggregate-' + i">
                            <p-select [options]="functionOptions" optionLabel="label" optionValue="value"
                                      [ngModel]="aggregate.fn" (ngModelChange)="updateAggregate(i, $event, null)"
                                      appendTo="body" styleClass="w-10rem"></p-select>
                            <p-select [options]="aggregateOptionsByFn[aggregate.fn]" optionLabel="label" optionValue="value"
                                      [ngModel]="aggregate.name" (ngModelChange)="updateAggregate(i, null, $event)"
                                      [filter]="true" filterPlaceholder="Suchen" emptyFilterMessage="Keine Treffer"
                                      emptyMessage="Keine Felder vorhanden" appendTo="body" styleClass="flex-1 min-w-0"></p-select>
                            <span class="text-sm text-color-secondary">als {{ aggregate.as }}</span>
                            <p-button icon="fa-regular fa-trash-can" severity="danger" [text]="true" [rounded]="true"
                                      ariaLabel="Kennzahl entfernen" (onClick)="removeAggregate(i)"></p-button>
                        </div>
                    } @empty {
                        <span class="text-sm text-color-secondary">Noch keine Kennzahl – z. B. „Anzahl“ hinzufügen.</span>
                    }
                    <div>
                        <p-button label="Kennzahl hinzufügen" icon="fa-solid fa-plus" size="small"
                                  severity="secondary" [outlined]="true" (onClick)="addAggregate()"></p-button>
                    </div>
                    <span class="text-sm text-color-secondary">
                        Erweiterte Felder und externe Zusatzfelder sind im Gruppieren-Modus nicht verfügbar.
                    </span>
                }
            }
        </div>

        <!-- node tree -->
        @if (rootContext) {
            <mvs-ql-builder-node [node]="model.root"
                                 [entityAlias]="model.root.entityName"
                                 [context]="rootContext"
                                 [isRoot]="true"
                                 (nodeChange)="onRootChange($event)"></mvs-ql-builder-node>
        }

        <!-- query-level conditions over ALL entities (WHERE on the root unit, may name any filterable join alias) -->
        <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg" data-test="ql-builder-conditions">
            <div class="flex flex-wrap align-items-center justify-content-between gap-2">
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-filter-list text-primary"></i>
                    <span class="font-medium">Bedingungen (alle Entitäten)</span>
                    @if (conditionsSummary.length + conditionRelativeChips.length; as count) {
                        <p-tag [value]="'' + count" severity="secondary" data-test="ql-builder-conditions-count"></p-tag>
                    }
                </div>
                <div class="flex flex-wrap align-items-center gap-2">
                    <p-button label="Bedingung bearbeiten" icon="fa-regular fa-pen-to-square" severity="secondary"
                              [outlined]="true" size="small" [disabled]="!filterableNodeCount || rootMetaState !== 'ready'"
                              [pTooltip]="textConditionsScope" tooltipPosition="bottom"
                              data-test="ql-builder-conditions-edit"
                              (onClick)="openConditionsDialog()"></p-button>
                    @if (conditionDateFieldOptions.length) {
                        <p-button label="Relativer Zeitraum" icon="fa-regular fa-calendar-days" severity="secondary"
                                  [outlined]="true" size="small" [disabled]="!!conditionRelativeDraft"
                                  [pTooltip]="textRelativeDate"
                                  data-test="ql-builder-conditions-relative-date-add"
                                  (onClick)="openConditionRelativeDate()"></p-button>
                    }
                    @if (hasConditions) {
                        <p-button label="Entfernen" icon="fa-regular fa-trash-can" severity="danger" [text]="true" size="small"
                                  pTooltip="Alle Bedingungen entfernen (die Filter an den Knoten bleiben)"
                                  data-test="ql-builder-conditions-remove"
                                  (onClick)="removeConditions()"></p-button>
                    }
                </div>
            </div>

            @if (conditionsSummary.length) {
                <ul class="list-none p-0 m-0 flex flex-column gap-1" data-test="ql-builder-conditions-summary">
                    @for (row of conditionsSummary; track $index; let i = $index) {
                        <li class="flex align-items-start gap-2 text-sm" [attr.data-test]="'ql-builder-condition-' + i">
                            <span class="text-xs font-bold text-color-secondary flex-shrink-0 w-3rem text-right pt-1">
                                {{ i === 0 ? 'WO' : 'UND' }}
                            </span>
                            <span>{{ row }}</span>
                        </li>
                    }
                </ul>
            }

            <!-- relative date conditions (chips) -->
            @if (conditionRelativeChips.length) {
                <div class="flex flex-wrap align-items-center gap-2" data-test="ql-builder-conditions-relative-date-chips">
                    @for (chip of conditionRelativeChips; track chip.index) {
                        <div class="flex align-items-center gap-1">
                            <p-chip [label]="chip.label" icon="fa-regular fa-calendar-days" [removable]="true"
                                    [pTooltip]="chip.value" [attr.data-test]="'ql-builder-conditions-relative-date-chip-' + chip.index"
                                    (onRemove)="removeConditionRelative(chip.index)"></p-chip>
                            @if (chip.editable) {
                                <p-button icon="fa-regular fa-pen" [text]="true" [rounded]="true" severity="secondary"
                                          size="small" pTooltip="Relativen Zeitraum ändern" ariaLabel="Relativen Zeitraum ändern"
                                          [disabled]="!!conditionRelativeDraft"
                                          (onClick)="editConditionRelative(chip.index)"></p-button>
                            }
                        </div>
                    }
                </div>
            }

            @if (!hasConditions) {
                <span class="text-sm text-color-secondary" data-test="ql-builder-conditions-empty">{{ textConditionsEmpty }}</span>
            }

            <!-- "Relativer Zeitraum" editor of the conditions: fields of every filterable alias, already qualified -->
            @if (conditionRelativeDraft; as draft) {
                <mvs-ql-builder-relative-date [fields]="conditionDateFieldOptions"
                                              alias=""
                                              [criterion]="draft.criterion"
                                              data-test="ql-builder-conditions-relative-date-editor"
                                              (apply)="onConditionRelativeApply($event)"
                                              (cancel)="cancelConditionRelativeDate()"></mvs-ql-builder-relative-date>
            }

            <ng-container *ngTemplateOutlet="conditionHintsTemplate; context: {hints: conditionHints, suffix: ''}"></ng-container>
        </div>

        <!-- sorting -->
        <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg">
            <span class="font-medium">Sortierung</span>
            @for (sorting of model.sortings ?? []; track $index; let i = $index) {
                <div class="flex flex-wrap align-items-center gap-2" [attr.data-test]="'ql-builder-sorting-' + i">
                    <p-select [options]="sortOptions" optionLabel="label" optionValue="value" filterBy="label,value"
                              [ngModel]="sorting.name" (ngModelChange)="updateSorting(i, $event, null)"
                              [filter]="true" filterPlaceholder="Suchen" emptyFilterMessage="Keine Treffer"
                              emptyMessage="Keine Sortierschlüssel vorhanden" appendTo="body" styleClass="flex-1 min-w-0">
                        <ng-template #selectedItem let-option>
                            <span>{{ option?.label }}</span>
                            <span class="text-sm text-color-secondary ml-2">{{ option?.value }}</span>
                        </ng-template>
                        <ng-template #item let-option>
                            <div class="flex flex-column">
                                <span>{{ option.label }}</span>
                                <span class="text-sm text-color-secondary">{{ option.value }}</span>
                            </div>
                        </ng-template>
                    </p-select>
                    <p-select [options]="directionOptions" optionLabel="label" optionValue="value"
                              [ngModel]="sorting.ascending" (ngModelChange)="updateSorting(i, null, $event)"
                              appendTo="body" styleClass="w-10rem"></p-select>
                    <p-button icon="fa-regular fa-trash-can" severity="danger" [text]="true" [rounded]="true"
                              ariaLabel="Sortierung entfernen" (onClick)="removeSorting(i)"></p-button>
                </div>
            } @empty {
                <span class="text-sm text-color-secondary">Keine Sortierung – die Reihenfolge bestimmt die Datenbank.</span>
            }
            <div class="flex align-items-center gap-2">
                <p-button label="Sortierung hinzufügen" icon="fa-solid fa-plus" size="small" severity="secondary"
                          [outlined]="true" [disabled]="!sortOptions.length" (onClick)="addSorting()"></p-button>
                <span class="text-sm text-color-secondary">Erweiterte Felder sind nicht sortierbar; externe Zusatzfelder nur im Zeilenmodus (nicht beim Gruppieren).</span>
            </div>
        </div>

        <!-- limit -->
        <div class="flex flex-wrap align-items-center gap-3 p-3 border-1 surface-border border-round-lg">
            <label for="qlBuilderLimit" class="font-medium">Zeilen (Limit)</label>
            <p-inputNumber inputId="qlBuilderLimit" [ngModel]="model.limit ?? limitsDefault"
                           (ngModelChange)="onLimitChange($event)"
                           [min]="1" [max]="maxLimit" [showButtons]="true" inputStyleClass="w-6rem"></p-inputNumber>
            <span class="text-sm text-color-secondary">
                höchstens {{ maxLimit }} {{ groupByMode ? '(gruppiert)' : '(Tabelle/Liste)' }}
            </span>
        </div>
    }
</div>

<!-- hints of a set of conditions (card: the model's; dialog: the draft's) -->
<ng-template #conditionHintsTemplate let-hints="hints" let-suffix="suffix">
    @if (hints.leftJoin.length) {
        <p-message severity="warn" styleClass="w-full" size="small" [attr.data-test]="'ql-builder-conditions-hint-left' + suffix">
            <span>Bedingung auf {{ hints.leftJoin.join(', ') }}: {{ textConditionLeftJoin }}</span>
        </p-message>
    }
    @if (hints.oneToN.length) {
        <p-message severity="warn" styleClass="w-full" size="small" [attr.data-test]="'ql-builder-conditions-hint-one-to-n' + suffix">
            <span>{{ textConditionOneToN }} ({{ hints.oneToN.join(', ') }})</span>
        </p-message>
    }
</ng-template>

<!-- conditions dialog: the shared condition builder in QL mode over a client-built form of ALL filterable aliases -->
@if (conditionsDialogVisible && model) {
    <p-dialog header="Bedingungen (alle Entitäten)"
              [visible]="conditionsDialogVisible"
              (visibleChange)="onConditionsDialogVisibleChange($event)"
              [modal]="true"
              [draggable]="false"
              [resizable]="false"
              appendTo="body"
              styleClass="ql-builder-conditions-dialog"
              data-test="ql-builder-conditions-dialog">
        <div class="flex flex-column gap-3" (keydown.escape)="cancelConditionsDialog()">
            <span class="text-sm text-color-secondary">{{ textConditionsScope }}</span>
            @switch (conditionsDialogState) {
                @case ('loading') {
                    <p-skeleton height="8rem" data-test="ql-builder-conditions-loading"></p-skeleton>
                }
                @case ('error') {
                    <p-message severity="error" size="small" styleClass="w-full" data-test="ql-builder-conditions-error"
                               text="Die Felder der Entitäten konnten nicht geladen werden."></p-message>
                }
                @case ('ready') {
                    @if (conditionsDialogForm && conditionsDialogWidgetData) {
                        <mvs-condition-builder [objectTypeAlias]="model.root.entityName"
                                               [form]="conditionsDialogForm"
                                               [widgetData]="conditionsDialogWidgetData"
                                               [filterCriteria]="conditionsDialogCriteria"
                                               (onChange)="onConditionsChange($event)"></mvs-condition-builder>
                    }
                }
            }
            <ng-container *ngTemplateOutlet="conditionHintsTemplate; context: {hints: conditionDialogHints, suffix: '-dialog'}"></ng-container>
        </div>
        <ng-template #footer>
            <div class="flex flex-wrap align-items-center justify-content-end gap-2 w-full">
                <p-button label="Abbrechen" severity="secondary" [text]="true"
                          data-test="ql-builder-conditions-cancel"
                          (onClick)="cancelConditionsDialog()"></p-button>
                <p-button label="Übernehmen" icon="fa-regular fa-check"
                          [disabled]="!conditionsDialogApplicable"
                          data-test="ql-builder-conditions-apply"
                          (onClick)="applyConditionsDialog()"></p-button>
            </div>
        </ng-template>
    </p-dialog>
}
